import Axios from 'axios'
import config from './config'

const apiUrls = {
  remote: config.studioRemoteApiUrl,
  local: config.studioLocalApiUrl
}

const saveUserPreferences = async ({ apiSource, apiKey, user }) => {
  try {
    let options = {
      method: 'patch',
      url: `${apiUrls[apiSource]}/saveUserPreferences/` + user.user_id,
      data: { preferences: user.preferences }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error saving user prefs" }
  }
}

const onboarding = async ({ apiSource, apiKey, user, client }) => {
  try {
    let options = {
      method: 'POST',
      url: `${apiUrls[apiSource]}/client/onboarding`,
      data: { user, client }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error onboarding" }
  }
}


const deleteAccount = async ({ apiSource, apiKey, deletionDate }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/client/deleteAccount/${deletionDate}`,
      method: "delete",
      data: {}
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error deleting account" }
  }
}

const recoverAccount = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/client/recoverAccount`,
      method: "delete",
      data: {}
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error recovering account" }
  }
}


const getPlans = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getPlans`,
      method: "get",
      data: {}
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error getting payment plans" }
  }
}

const getUsage = async ({ apiSource, apiKey, history, billingPeriod }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getUsage`,
      method: "get",
      data: {}
    }

    if (history) options.data.history = true
    if (billingPeriod) options.data.billing_period = billingPeriod

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error getting usage data" }
  }
}

const changePlan = async ({ apiSource, apiKey, new_tier }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/changePlan/${new_tier}`,
      method: "patch",
      data: {}
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error changing payment plan" }
  }
}


const cancelChangePlan = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/cancelChangePlan`,
      method: "patch",
      data: {}
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error cancelling payment plan change" }
  }
}

const getClientPaymentCard = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getClientPaymentCard`,
      method: "get",
      data: {}
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error getting payment card data" }
  }
}


const setupStripeSession = async ({ apiSource, apiKey, stripe_options }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/setupStripeSession`,
      method: "post",
      data: stripe_options
    }

    const apiMode = window.location.hostname === "local.smartix.uk" ? "test" : "live"

    return await callApi({ apiKey, options, apiMode })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error setting up stripe session" }
  }
}


const getStripeSessionResponse = async ({ apiSource, apiKey, stripeSessionId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getStripeSessionResponse/${stripeSessionId}`,
      method: "get"
    }

    const apiMode = window.location.hostname === "local.smartix.uk" ? "test" : "live"

    return await callApi({ apiKey, options, apiMode })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error getting stripe response" }
  }
}

const getStripePublicKey = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/getStripePublicKey`,
      method: "get"
    }

    const apiMode = window.location.hostname === "local.smartix.uk" ? "test" : "live"

    return await callApi({ apiKey, options, apiMode })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error getting stripe public key" }
  }
}



const applyForMonthlyPlan = async ({ apiSource, apiKey, application }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/billing/applyForMonthlyPlan`,
      method: "put",
      data: {
        application
      }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error applying for monthly contract" }
  }
}


const sendSupportRequest = async ({ apiSource, apiKey, subject, regarding, message }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/sendSupportRequest`,
      method: "post",
      data: {
        subject,
        regarding,
        message
      }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error sending support request" }
  }
}

const getSupportThreads = async ({ apiSource, apiKey }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/getSupportThreads`,
      method: "get"
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error getting support threads" }
  }
}

const getSupportThreadMessages = async ({ apiSource, apiKey, thread_id }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/getSupportThreadMessages/${thread_id}`,
      method: "get"
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error getting support message" }
  }
}


const closeSupportThread = async ({ apiSource, apiKey, thread_id }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/closeSupportThread/${thread_id}`,
      method: "patch"
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error closing support thread" }
  }
}

const replyToSupportThread = async ({ apiSource, apiKey, thread_id, message, close }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/support/replyToSupportThread/${thread_id}`,
      method: "patch",
      data: {
        message,
        close
      }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error replying to support thread" }
  }
}



const createTestStampcard = async ({ apiSource, apiKey, templateId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/stampcard/create/${templateId}`,
      method: "put",
      data: {}
    }

    return await callApi({ apiKey, apiMode: "test", options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error creating test stampcard " }
  }
}


const createTestLoyaltycard = async ({ apiSource, apiKey, templateId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/loyalty/create/${templateId}`,
      method: "put",
      data: {}
    }

    return await callApi({ apiKey, apiMode: "test", options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error creating test loyalty card " }
  }
}


const createGiftcard = async ({ apiSource, apiKey, apiMode, templateId, amount, expiry }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/giftcard/create/${templateId}`,
      method: "put",
      data: { amount, expiry }
    }

    return await callApi({ apiKey, apiMode, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error creating test giftcard" }
  }
}

const changeGiftcardBalance = async ({ apiSource, apiKey, templateId, passId, action, amount }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/giftcard/changeBalance/${templateId}/${passId}`,
      method: "patch",
      data: { amount, action }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error changing giftcard balance" }
  }
}


const changeStampcardStamps = async ({ apiSource, apiKey, templateId, passId, action, amount, note }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/stampcard/changeStamps/${templateId}/${passId}`,
      method: "patch",
      data: { amount, action, note }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error changing stampcard stamps" }
  }
}

const changeLoyaltyPoints = async ({ apiSource, apiKey, templateId, passId, action, amount, note }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/loyalty/changePoints/${templateId}/${passId}`,
      method: "patch",
      data: { amount, action, note }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error changing loyalty points " }
  }
}



const setScannerPassExpiry = async ({ apiSource, apiKey, templateId, passId, expiry }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/scanner/setExpiryDate/${templateId}/${passId}`,
      method: "patch",
      data: { expiry }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error changing pass expiry date " }
  }
}


const toggleScannerPassEnabled = async ({ apiSource, apiKey, templateId, passId, enabled, note }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/scanner/toggleEnabled/${templateId}/${passId}`,
      method: "patch",
      data: { enabled, note }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error toggle pass enabled state" }
  }
}


const changeEventDate = async ({ apiSource, apiKey, templateId, newEventDate, newExpiryDate, notification }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/changeEventDate/${templateId}`,
      method: "patch",
      data: { newEventDate, newExpiryDate, notification }
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error changing event date" }
  }
}



const getSmartixScannerPassHistory = async ({ apiSource, apiKey, passType, templateId, passId }) => {
  try {
    let options = {
      url: `${apiUrls[apiSource]}/getSmartixScannerPassHistory/${passType}/${templateId}/${passId}`,
      method: "get"
    }

    return await callApi({ apiKey, options })
  }
  catch (err) {
    console.error(err)
    return { ok: false, error: err.response?.data?.error?.message ?? "Error passing scan history" }
  }
}


const callApi = async ({ apiKey, apiMode, options }) => {
  try {
    options.headers = {
      'x-api-key': apiKey,
      'x-mode' : apiMode,
      'Content-type': 'application/json',
    }

    options.withCredentials = true

    let response = await Axios(options)

    return response.data
  }
  catch (err) {
    
    throw err
  }
}


export {
  //client
  deleteAccount,
  recoverAccount,
  saveUserPreferences,
  onboarding,

  // billing
  applyForMonthlyPlan,
  cancelChangePlan,
  changePlan,
  getClientPaymentCard,
  getPlans,
  getStripePublicKey,
  getStripeSessionResponse,
  setupStripeSession,
  getUsage,

  // supports
  closeSupportThread,
  getSupportThreadMessages,
  getSupportThreads,
  replyToSupportThread,
  sendSupportRequest,

  // Passes
  changeEventDate,
  changeGiftcardBalance,
  changeStampcardStamps,
  changeLoyaltyPoints,
  createGiftcard,
  createTestStampcard,
  createTestLoyaltycard,
  getSmartixScannerPassHistory,
  setScannerPassExpiry,
  toggleScannerPassEnabled,
}